/**
 * Multi-UPN identity resolution — shared, pure helpers (v1.135.0).
 *
 * A person is not one UPN. Sign-ins are logged under the Entra user_principal_name
 * while Exchange logs mailbox activity under the mail attribute, and the two differ
 * for roughly one user in six. Each investigation run resolves the typed identifier
 * to its directory object and searches every address that object answers to; this
 * module shapes that result for the UI and decides which events to flag as logged
 * under an alternate address.
 *
 * Kept free of React so `frontend/scripts/identityResolution.test.mjs` can exercise it.
 */

/** Resolution states where alias expansion did NOT happen and the analyst must be told. */
export const UNRESOLVED_METHODS = ['unresolved', 'ambiguous', 'query_failure', 'skipped_no_token'];

const str = (v) => (v === null || v === undefined ? '' : String(v));
const lower = (v) => str(v).trim().toLowerCase();

/**
 * Normalize the runbook's `identityResolution` blob field to PascalCase.
 *
 * Returns null when the field is absent — that means the run predates this feature
 * (blobs before v1.135.0), which is different from a run that looked and failed.
 * Callers render nothing for null and a warning for `Resolved: false`.
 */
export function normalizeIdentityResolution(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const identifiers = [];
  const seen = new Set();
  const rawIds = raw.identifiers ?? raw.Identifiers;
  if (Array.isArray(rawIds)) {
    for (const id of rawIds) {
      const v = lower(id);
      if (v && !seen.has(v)) { seen.add(v); identifiers.push(v); }
    }
  }

  return {
    Resolved: (raw.resolved ?? raw.Resolved) === true,
    Method: str(raw.method ?? raw.Method),
    TypedUpn: str(raw.typedUpn ?? raw.TypedUpn),
    PrimaryUpn: str(raw.primaryUpn ?? raw.PrimaryUpn),
    EntraObjectId: str(raw.entraObjectId ?? raw.EntraObjectId),
    Mail: str(raw.mail ?? raw.Mail),
    DisplayName: str(raw.displayName ?? raw.DisplayName),
    // Tri-state: true / false / null ("the directory did not say"). Coalesce with ??
    // so a genuine false is preserved.
    OnPremisesSyncEnabled: raw.onPremisesSyncEnabled ?? raw.OnPremisesSyncEnabled ?? null,
    Identifiers: identifiers,
    SnapshotDate: str(raw.snapshotDate ?? raw.SnapshotDate),
    SnapshotAgeDays: raw.snapshotAgeDays ?? raw.SnapshotAgeDays ?? null,
    Stale: (raw.stale ?? raw.Stale) === true,
    Notes: str(raw.notes ?? raw.Notes),
  };
}

/** Addresses searched for this case other than the primary (sign-in) UPN. */
export function aliasIdentifiers(identity) {
  if (!identity) return [];
  const primary = lower(identity.PrimaryUpn);
  return (identity.Identifiers || []).filter((id) => id && id !== primary);
}

/**
 * True when this event was logged under one of the user's OTHER addresses.
 *
 * Requires membership in the resolved set, so an unrelated address (or a service
 * principal GUID in a caller field) never gets flagged as this person's alias.
 */
export function isAliasIdentityEvent(event, identity) {
  if (!event || !identity || !identity.Resolved) return false;
  const primary = lower(identity.PrimaryUpn);
  if (!primary) return false;
  const upn = lower(event.UserPrincipalName);
  if (!upn || upn === primary) return false;
  return (identity.Identifiers || []).includes(upn);
}

/**
 * Banner copy for the case view. Returns null when there is nothing to say —
 * either the run predates the feature, or resolution succeeded on a fresh snapshot.
 *
 * `tone` is 'warning' when the search may be under-scoped (the analyst must not read
 * an empty tab as "nothing happened"), 'info' when the search is complete but the
 * directory data behind it deserves a caveat.
 */
export function identityBannerState(identity) {
  if (!identity) return null;
  const { Method, Resolved, TypedUpn, Stale, SnapshotAgeDays, Notes } = identity;

  if (!Resolved || UNRESOLVED_METHODS.includes(Method)) {
    const who = TypedUpn || 'this identifier';
    let title = 'Searched one address only — results may be incomplete';
    let body;
    switch (Method) {
      case 'ambiguous':
        title = 'Identity is ambiguous — searched one address only';
        body = `More than one directory account answers to ${who}. Rather than risk pulling another person's activity into this case, only the address as typed was searched. If this user also has a separate email address, its activity is NOT included.`;
        break;
      case 'query_failure':
        body = `The Entra directory lookup failed, so this user's other addresses could not be found. Only ${who} was searched. If their mailbox logs under a different address, that activity is missing — re-run the investigation to try again.`;
        break;
      case 'skipped_no_token':
        body = `The directory was unreachable during this run, so this user's other addresses could not be found. Only ${who} was searched.`;
        break;
      default:
        body = `${who} was not found in this tenant's Entra user directory, so no alternate addresses could be resolved. Only the address as typed was searched. Shared, room and resource mailboxes are not in the user directory — if this is one, its activity may be logged under a different address.`;
        break;
    }
    return { tone: 'warning', title, body, notes: Notes };
  }

  if (Method === 'historical_unique') {
    return {
      tone: 'info',
      title: 'Resolved from directory history',
      body: 'This account is not in the current directory snapshot — it may have been deleted. Addresses were resolved from the last snapshot that contained it.',
      notes: Notes,
    };
  }

  if (Stale) {
    const age = typeof SnapshotAgeDays === 'number' ? `${SnapshotAgeDays} days old` : 'out of date';
    return {
      tone: 'info',
      title: `Directory snapshot is ${age}`,
      body: "Addresses were resolved from the most recent directory snapshot available. A recent address change may not be reflected.",
      notes: Notes,
    };
  }

  return null;
}
